import { Injectable, NgZone } from '@angular/core'
import {
    NeuroglancerNullarySignal,
    NeuroglancerSignal,
    NeuroglancerWatchableValueInterface,
} from '../../shared/model/neuroglancer-type-mappings'
import { fromEventPattern, map, Observable, share, shareReplay, startWith } from 'rxjs'
import { runInZone } from '../../shared/utils/run-in-zone'

@Injectable({
    providedIn: 'root',
})
export class TrackableService {
    constructor(private zone: NgZone) {}

    observeValue<T>(trackable: NeuroglancerWatchableValueInterface<T>): Observable<T> {
        return fromEventPattern<void>(
            handler => trackable.changed.add(handler),
            handler => trackable.changed.remove(handler)
        ).pipe(
            map(() => trackable.value),
            startWith(trackable.value),
            shareReplay(1),
            runInZone(this.zone)
        )
    }

    observeEvent(nullary: NeuroglancerNullarySignal): Observable<void> {
        return fromEventPattern<void>(
            handler => nullary.add(handler),
            handler => nullary.remove(handler)
        ).pipe(runInZone(this.zone), share())
    }

    observeSignal<T extends unknown[]>(
        signal: NeuroglancerSignal<(...args: [...T]) => void>
    ): Observable<[...T]> {
        return fromEventPattern<[...T]>(
            handler => signal.add(handler),
            handler => signal.remove(handler),
            (...args: [...T]) => [...args]
        ).pipe(runInZone(this.zone), share())
    }

    wrapWatchableValue<T>(watchable: NeuroglancerWatchableValueInterface<T>): WatchableValue<T> {
        return new WatchableValueSimple<T>(watchable, this.observeValue(watchable))
    }

    wrapWatchableValueWithConversion<N, A>(
        watchable: NeuroglancerWatchableValueInterface<N>,
        convert: { fromNeuroglancer: (value: N) => A; toNeuroglancer: (value: A) => N }
    ): WatchableValue<A> {
        return new ConvertedWatchableValue<N, A>(
            new WatchableValueSimple<N>(watchable, this.observeValue(watchable)),
            convert
        )
    }
}

export interface WatchableValue<T> {
    setValue: (value: T) => void
    value$: Observable<T>
}

class WatchableValueSimple<T> implements WatchableValue<T> {
    constructor(
        private readonly _watchable: NeuroglancerWatchableValueInterface<T>,
        public readonly value$: Observable<T>
    ) {}

    get value(): T {
        return this._watchable.value
    }

    public setValue(value: T): void {
        if (this._watchable.value !== value) {
            this._watchable.value = value
        }
    }
}

class ConvertedWatchableValue<N, A> implements WatchableValue<A> {
    constructor(
        private _watchableNg: WatchableValue<N>,
        private _convert: { fromNeuroglancer: (value: N) => A; toNeuroglancer: (value: A) => N }
    ) {}

    public value$ = this._watchableNg.value$.pipe(map(this._convert.fromNeuroglancer))

    public setValue(value: A): void {
        this._watchableNg.setValue(this._convert.toNeuroglancer(value))
    }
}
